/**
 * Belt Configuration Service
 * Fetches belt configuration data from Firebase /belts path (NOT /belt)
 * Matches original Marune app behavior from beltData.service.js
 */
import { database } from './core';
import { ref, get, onValue, off } from 'firebase/database';
import logger from '../../utils/logger';
import { BeltConfiguration, BeltRank, SupportedColors } from '../../types/belt';
import { BELT_COLORS, CONTRAST_TYPES } from '../../utils/belt-colors';

/**
 * Default belt configuration used when Firebase data is unavailable
 * Matches original app's beltReducer initialState
 */
const DEFAULT_BELT_CONFIG: BeltConfiguration = {
  martialArts: {
    "I am a spectator": [
      {
        name: 'Spectator',
        level: 'adult',
        baseColor: 'white'
      }
    ],
    'Brazilian Jiu Jitsu': [
      // White belt variants
      { name: 'White', level: 'adult', baseColor: 'white' },
      { name: 'White', rank: '1st Stripe', level: 'adult', baseColor: 'white', stripeCount: 1, stripeColor: 'white', stripeBgColor: 'black' },
      { name: 'White', rank: '2nd Stripe', level: 'adult', baseColor: 'white', stripeCount: 2, stripeColor: 'white', stripeBgColor: 'black' },
      { name: 'White', rank: '3rd Stripe', level: 'adult', baseColor: 'white', stripeCount: 3, stripeColor: 'white', stripeBgColor: 'black' },
      { name: 'White', rank: '4th Stripe', level: 'adult', baseColor: 'white', stripeCount: 4, stripeColor: 'white', stripeBgColor: 'black' },
      // Blue belt variants
      { name: 'Blue', level: 'adult', baseColor: 'blue' },
      { name: 'Blue', rank: '1st Stripe', level: 'adult', baseColor: 'blue', stripeCount: 1, stripeColor: 'white', stripeBgColor: 'black' },
      { name: 'Blue', rank: '2nd Stripe', level: 'adult', baseColor: 'blue', stripeCount: 2, stripeColor: 'white', stripeBgColor: 'black' },
      { name: 'Blue', rank: '3rd Stripe', level: 'adult', baseColor: 'blue', stripeCount: 3, stripeColor: 'white', stripeBgColor: 'black' },
      { name: 'Blue', rank: '4th Stripe', level: 'adult', baseColor: 'blue', stripeCount: 4, stripeColor: 'white', stripeBgColor: 'black' },
      // Purple belt variants
      { name: 'Purple', level: 'adult', baseColor: 'purple' },
      { name: 'Purple', rank: '1st Stripe', level: 'adult', baseColor: 'purple', stripeCount: 1, stripeColor: 'white', stripeBgColor: 'black' },
      { name: 'Purple', rank: '2nd Stripe', level: 'adult', baseColor: 'purple', stripeCount: 2, stripeColor: 'white', stripeBgColor: 'black' },
      { name: 'Purple', rank: '3rd Stripe', level: 'adult', baseColor: 'purple', stripeCount: 3, stripeColor: 'white', stripeBgColor: 'black' },
      { name: 'Purple', rank: '4th Stripe', level: 'adult', baseColor: 'purple', stripeCount: 4, stripeColor: 'white', stripeBgColor: 'black' },
      // Brown belt variants
      { name: 'Brown', level: 'adult', baseColor: 'brown' },
      { name: 'Brown', rank: '1st Stripe', level: 'adult', baseColor: 'brown', stripeCount: 1, stripeColor: 'white', stripeBgColor: 'black' },
      { name: 'Brown', rank: '2nd Stripe', level: 'adult', baseColor: 'brown', stripeCount: 2, stripeColor: 'white', stripeBgColor: 'black' },
      { name: 'Brown', rank: '3rd Stripe', level: 'adult', baseColor: 'brown', stripeCount: 3, stripeColor: 'white', stripeBgColor: 'black' },
      { name: 'Brown', rank: '4th Stripe', level: 'adult', baseColor: 'brown', stripeCount: 4, stripeColor: 'white', stripeBgColor: 'black' },
      // Black belt variants (stripes on red background)
      { name: 'Black', level: 'adult', baseColor: 'black' },
      { name: 'Black', rank: '1st Stripe', level: 'adult', baseColor: 'black', stripeCount: 1, stripeColor: 'white', stripeBgColor: 'red' },
      { name: 'Black', rank: '2nd Stripe', level: 'adult', baseColor: 'black', stripeCount: 2, stripeColor: 'white', stripeBgColor: 'red' },
      { name: 'Black', rank: '3rd Stripe', level: 'adult', baseColor: 'black', stripeCount: 3, stripeColor: 'white', stripeBgColor: 'red' },
      { name: 'Black', rank: '4th Stripe', level: 'adult', baseColor: 'black', stripeCount: 4, stripeColor: 'white', stripeBgColor: 'red' },
      { name: 'Black', rank: '5th Stripe', level: 'adult', baseColor: 'black', stripeCount: 5, stripeColor: 'white', stripeBgColor: 'red' },
      { name: 'Black', rank: '6th Stripe', level: 'adult', baseColor: 'black', stripeCount: 6, stripeColor: 'white', stripeBgColor: 'red' },
      // Coral belt (black/red alternating)
      { name: 'Black / Red', rank: '7th Stripe', level: 'adult', baseColor: 'black', contrastType: 'alternate', contrastColor: 'red', stripeCount: 7, stripeColor: 'white', stripeBgColor: 'red' },
      // Red/White belt
      { name: 'Red / White', rank: '8th Stripe', level: 'adult', baseColor: 'white', contrastType: 'alternate', contrastColor: 'red', stripeCount: 8, stripeColor: 'white', stripeBgColor: 'black' },
      // Red belt (grandmaster)
      { name: 'Red', rank: '10th Stripe', level: 'adult', baseColor: 'red', stripeCount: 10, stripeColor: 'white', stripeBgColor: 'black' }
    ]
  },
  orderedMA: ["I am a spectator", 'Brazilian Jiu Jitsu'],
  supportedValues: {
    colors: {
      white: '#ffffff',
      yellow: '#FED83A',
      orange: '#FF7728',
      green: '#289234',
      blue: '#0012B0',
      red: '#FF0011',
      purple: '#9B00FF',
      brown: '#924D20',
      black: '#000000',
      grey: '#CCCCCC',
      none: 'transparent'
    },
    contrastTypes: [...CONTRAST_TYPES]
  },
  martialArtsWithGi: [
    'Brazilian Jiu Jitsu',
    'Ju Jitsu',
    'Judo',
    'Japanese Jiu Jitsu'
  ],
  syncedWithDB: false
};

/**
 * Fetch belt configuration from Firebase /belts path
 * This is the primary source of truth for belt data
 */
export const fetchBeltConfiguration = async (): Promise<BeltConfiguration> => {
  try {
    logger.info('Fetching belt configuration from Firebase /belts...');

    if (!database) {
      logger.error('Firebase database not initialized');
      return DEFAULT_BELT_CONFIG;
    }

    // IMPORTANT: Use /belts path (NOT /belt) to match original app
    const beltsRef = ref(database, 'belts');
    const snapshot = await get(beltsRef);

    if (snapshot.exists()) {
      const data = snapshot.val();
      logger.info('Belt configuration fetched successfully from /belts');

      // Extract martialArts, orderedMA, and supportedValues
      const martialArts = data.martialArts || {};
      const orderedMA = data.orderedMA || [];
      const supportedValues = data.supportedValues || {
        colors: DEFAULT_BELT_CONFIG.supportedValues.colors,
        contrastTypes: DEFAULT_BELT_CONFIG.supportedValues.contrastTypes
      };

      // Build martialArtsWithGi from data or use default
      const martialArtsWithGi: string[] = [];
      if (martialArts) {
        Object.entries(martialArts).forEach(([name, details]: [string, any]) => {
          // Check if this martial art has gi-related belts
          if (Array.isArray(details)) {
            // It's an array of belt ranks - check if name includes gi-based arts
            if (['Brazilian Jiu Jitsu', 'Ju Jitsu', 'Judo', 'Japanese Jiu Jitsu', 'Karate', 'Taekwondo', 'Aikido'].includes(name)) {
              martialArtsWithGi.push(name);
            }
          } else if (details.hasGi === true) {
            martialArtsWithGi.push(name);
          }
        });
      }

      return {
        martialArts,
        orderedMA,
        supportedValues: {
          colors: supportedValues.colors || DEFAULT_BELT_CONFIG.supportedValues.colors,
          contrastTypes: supportedValues.contrastTypes || DEFAULT_BELT_CONFIG.supportedValues.contrastTypes
        },
        martialArtsWithGi: martialArtsWithGi.length > 0 ? martialArtsWithGi : DEFAULT_BELT_CONFIG.martialArtsWithGi,
        syncedWithDB: true
      };
    }

    logger.info('No belt configuration found in /belts, using defaults');
    return DEFAULT_BELT_CONFIG;
  } catch (error: any) {
    logger.error('Error fetching belt configuration:', error?.message || error);
    return DEFAULT_BELT_CONFIG;
  }
};

/**
 * Subscribe to belt configuration changes in real-time
 * @param callback Function called when configuration changes
 * @returns Unsubscribe function
 */
export const subscribeToBeltConfiguration = (
  callback: (config: BeltConfiguration) => void
): (() => void) => {
  if (!database) {
    logger.error('Firebase database not initialized');
    callback(DEFAULT_BELT_CONFIG);
    return () => {};
  }

  const beltsRef = ref(database, 'belts');

  const handleSnapshot = (snapshot: any) => {
    if (snapshot.exists()) {
      const data = snapshot.val();
      const martialArts = data.martialArts || {};
      const orderedMA = data.orderedMA || [];
      const supportedValues = data.supportedValues || DEFAULT_BELT_CONFIG.supportedValues;

      const martialArtsWithGi: string[] = [];
      Object.entries(martialArts).forEach(([name, details]: [string, any]) => {
        if (details.hasGi === true ||
            ['Brazilian Jiu Jitsu', 'Ju Jitsu', 'Judo', 'Japanese Jiu Jitsu', 'Karate', 'Taekwondo', 'Aikido'].includes(name)) {
          martialArtsWithGi.push(name);
        }
      });

      callback({
        martialArts,
        orderedMA,
        supportedValues: {
          colors: supportedValues.colors || DEFAULT_BELT_CONFIG.supportedValues.colors,
          contrastTypes: supportedValues.contrastTypes || DEFAULT_BELT_CONFIG.supportedValues.contrastTypes
        },
        martialArtsWithGi: martialArtsWithGi.length > 0 ? martialArtsWithGi : DEFAULT_BELT_CONFIG.martialArtsWithGi,
        syncedWithDB: true
      });
    } else {
      callback(DEFAULT_BELT_CONFIG);
    }
  };

  onValue(beltsRef, handleSnapshot, (error) => {
    logger.error('Belt configuration subscription error:', error);
    callback(DEFAULT_BELT_CONFIG);
  });

  return () => off(beltsRef);
};

/**
 * Get the list of supported belt colors
 */
export const getSupportedColors = (): SupportedColors => {
  return DEFAULT_BELT_CONFIG.supportedValues.colors;
};

/**
 * Get the list of contrast types for belt patterns
 */
export const getContrastTypes = (): string[] => {
  return [...CONTRAST_TYPES];
};

/**
 * Get all predefined belts for a specific martial art
 * @param config The belt configuration
 * @param martialArt The martial art name
 * @returns Array of belt ranks, or empty array if not found
 */
export const getBeltsForMartialArt = (
  config: BeltConfiguration,
  martialArt: string
): BeltRank[] => {
  const belts = config.martialArts[martialArt];
  if (Array.isArray(belts)) {
    return belts;
  }
  // If no belts defined for this martial art, return empty array
  // User can create custom belts
  return [];
};

/**
 * Get belts filtered by level (adult vs junior)
 */
export const getBeltsByLevel = (
  config: BeltConfiguration,
  martialArt: string,
  level: 'adult' | 'junior'
): BeltRank[] => {
  const allBelts = getBeltsForMartialArt(config, martialArt);
  return allBelts.filter(belt => belt.level === level);
};

/**
 * Get the hex color code for a belt color name
 */
export const getColorHex = (colorName: string): string => {
  const lowerName = colorName.toLowerCase();

  // Check in BELT_COLORS utility
  if (BELT_COLORS[lowerName]) {
    return BELT_COLORS[lowerName].backgroundColorCode;
  }

  // Check in default supported colors
  if (DEFAULT_BELT_CONFIG.supportedValues.colors[lowerName]) {
    return DEFAULT_BELT_CONFIG.supportedValues.colors[lowerName];
  }

  // If it's already a hex color, return as-is
  if (colorName.startsWith('#')) {
    return colorName;
  }

  // Default to white
  return '#ffffff';
};

/**
 * Create a display name for a belt configuration
 * e.g., "Purple Belt - 2nd Stripe" or "Black / Red Belt"
 */
export const getBeltDisplayName = (beltConfig: BeltRank): string => {
  let displayName = beltConfig.name;

  // Add "Belt" suffix if not already present
  if (!displayName.toLowerCase().includes('belt')) {
    displayName += ' Belt';
  }

  // Add rank/stripe info if present
  if (beltConfig.rank) {
    displayName += ` - ${beltConfig.rank}`;
  }

  return displayName;
};

/**
 * Get the default belt configuration (for testing or fallback)
 */
export const getDefaultBeltConfiguration = (): BeltConfiguration => {
  return { ...DEFAULT_BELT_CONFIG };
};
